import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import {
  CountriesService,
  AgentsService,
  AgentsCacheService,
  DistributorsService,
  ProfileService,
  LocalesService,
  PopUpService,
  TasksService,
  CoreQuestionsFactoryService,
  UtilsQuestionsService
} from 'core/services';
import { Client, Distributor } from 'core/models';
import {
  QuestionBase,
  QuestionGroup,
  TextboxQuestion,
  ImagecropperQuestion,
  DropdownQuestion,
  AgentsQuestion,
  TextareaQuestion,
  Decorator,
  CheckboxQuestion,
  HiddenQuestion
} from 'core/components/forms';
import { PlansService } from 'core/services/plans.service';
import { DEFAULT_NOTIFICATIONS_CFG, NO_EDITOR_PLAN, TERMS_URL_LANGS } from '../assets/constants';
import { DateTime } from 'luxon';
import { BehaviorSubject } from 'rxjs';
import { UntypedFormControl, Validators } from '@angular/forms';

@Injectable()
export class ClientsQuestionsService {
  userRole;
  questions: QuestionBase<any>[];
  private editorAccess;
  private canEditClient: boolean;
  private canEditPlan: boolean;
  private isGamelearnAgent: boolean;
  private isPartnerAgent: boolean;
  private isCsOrRoot: boolean;
  private canCreateScorm: boolean;
  private isTermsEnabled: boolean = false;
  private activationQuestionGroup: QuestionGroup;
  private dataQuestionGroup: QuestionGroup;
  private catalogueQuestionGroup: QuestionGroup;
  private configQuestionGroup: QuestionGroup;
  private notificationsQuestionGroup: QuestionGroup;
  private adminsQuestionGroup: QuestionGroup;
  private planQuestionGroup: QuestionGroup;
  private subscriptionQuestionGroup: QuestionGroup;
  protected timezoneSubject = new BehaviorSubject(null);
  protected gamificationSubject = new BehaviorSubject(null);
  protected scormClientSubject = new BehaviorSubject(null);

  private notificationsCfg: any = {
    n1: { enabled: true },
    n3: { enabled: true, weekPeriodicity: 2 },
    n4: { enabled: true },
    n6: { enabled: true, includeSurvey: true },
    n8: { enabled: true },
    push: { enabled: true }
  };
  private referralsCfg: boolean = true;
  private isGamificationEnabled: boolean = false;

  constructor(
    public countries: CountriesService,
    public agents: AgentsService,
    public agentsCache: AgentsCacheService,
    public distributors: DistributorsService,
    public plansService: PlansService,
    public locales: LocalesService,
    public profileService: ProfileService,
    public translateService: TranslateService,
    public popupService: PopUpService,
    public tasksService: TasksService,
    public questionsFactory: CoreQuestionsFactoryService,
    private utilsQuestionsService: UtilsQuestionsService
  ) {
    profileService.getLoggedUserRole$().subscribe((userRole) => {
      this.userRole = userRole;
      this.isGamelearnAgent = ['global', 'root'].includes(this.userRole.scope);
      this.isPartnerAgent = ['partner'].includes(this.userRole.scope);
      this.canEditClient =
        userRole.group === 'cs' ||
        userRole.name === 'distributorManager' ||
        ['partner', 'root'].includes(this.userRole.scope);
      this.canEditPlan = this.isGamelearnAgent;
      this.isCsOrRoot = userRole.group === 'cs' || userRole.scope === 'root';
      this.canCreateScorm = this.profileService.canExecute('clients.createScormClient');
    });
  }

  getQuestions(client: Client, distributor: Distributor, catalogueData: any) {
    this.editorAccess = (client && client.plan && client.plan.editorAccess) || false;
    this.agentsCache.updateDistributorAgents(client ? client.distributorId : distributor ? distributor.id : null);
    distributor = !distributor && client && client.partner ? client.partner : distributor;

    if (client) {
      this.timezoneSubject.next(client.timezone);
      this.isGamificationEnabled = client.gamification.enabled;
      this.notificationsCfg = client.notificationsCfg || DEFAULT_NOTIFICATIONS_CFG;
      this.referralsCfg = client.referrals || true;
    }

    const questions: QuestionBase<any>[] = [
      this.getClientDataQuestions(client, distributor),
      this.getConfigurationQuestions(client, distributor),
      this.getSubscriptionActivationQuestions(client),
      this.getNotificationsConfig(client, distributor),
      this.getRankingQuestion(client, distributor),
      this.getCatalogueQuestionGroup(client, catalogueData),
      this.getTermsQuestion(client),
      this.getAgentsQuestions(client)
    ];
    this.questions = questions.filter((q) => !!q);
    return this.questions;
  }

  changeSubscriptions(client) {
    this.popupService
      .confirm({
        title: this.translateService.instant(
          client.subscriptions.credits ? 'subscriptions.changeToUsers' : 'subscriptions.changeToCredits'
        ),
        html: this.translateService.instant('subscriptions.changeMessage'),
        type: 'question'
      })
      .then(() => {
        this.tasksService.execute('change-client-subscription', { clientId: client.id });
      })
      .catch(() => {});
  }

  getClientDataQuestions(client?: Client, distributor?: Distributor) {
    const isExpertClient = distributor && distributor.type === 'expert';
    this.dataQuestionGroup = new QuestionGroup({
      title: 'clients.clientInfo',
      isSection: true,
      childQuestions: [
        this.getDistributorQuestion(client, distributor),
        new TextboxQuestion({
          key: 'name',
          label: 'nouns.name',
          class: 'col-1',
          value: client ? client.name : null,
          validators: {
            required: true,
            function: (formControl: UntypedFormControl) => {
              return formControl.value && formControl.value.trim() ? null : { required: false };
            }
          },
          editable: this.canEditClient
        }),
        new TextboxQuestion({
          key: 'displayName',
          label: 'nouns.displayName',
          class: 'col-1',
          value: client ? client.displayName : null,
          validators: {
            required: true,
            function: (formControl: UntypedFormControl) => {
              return formControl.value && formControl.value.trim() ? null : { required: false };
            }
          },
          editable: this.canEditClient
        }),
        this.isGamelearnAgent || this.isPartnerAgent
          ? new DropdownQuestion({
              key: 'countryId',
              class: 'col-2',
              label: 'nouns.country',
              value: client ? client.countryId : 'ESP',
              options: this.countries.getOptions(),
              validators: { required: true },
              disabled: !this.canEditClient,
              eventHandlers: {
                onChange: (value) => {
                  const countryId = value;
                  const index = this.utilsQuestionsService.findQuestionIndex(
                    'timezone',
                    this.dataQuestionGroup.childQuestions
                  );
                  const question: DropdownQuestion = this.dataQuestionGroup.childQuestions[index];
                  question.options = this.countries.getTimezoneOptions(countryId);
                  question.control.setValue(this.countries.getDefaultTimezone(countryId));
                }
              }
            })
          : null,
        this.isGamelearnAgent || this.isPartnerAgent
          ? new DropdownQuestion({
              key: 'timezone',
              class: 'col-2',
              label: 'nouns.timezone',
              value: client ? client.timezone : this.countries.getDefaultTimezone('ESP'),
              options: client
                ? this.countries.getTimezoneOptions(client.countryId)
                : this.countries.getTimezoneOptions('ESP'),
              disabled: !this.canEditClient,
              validators: { required: true },
              eventHandlers: {
                onChange: (selectedTimezone) => {
                  this.timezoneSubject.next(selectedTimezone);
                }
              }
            })
          : null,
        new TextboxQuestion({
          key: 'cid',
          class: 'col-2-offset',
          label: 'nouns.cid',
          value: client ? client.cid : null,
          validators: { required: false },
          editable: this.canEditClient
        }),
        this.isGamelearnAgent
          ? new TextboxQuestion({
              key: 'crmId',
              class: 'col-2-offset',
              label: 'nouns.crmId',
              value: client ? client.crmId : null,
              validators: { required: false },
              editable: this.canEditClient
            })
          : null,

        !isExpertClient
          ? new ImagecropperQuestion({
              key: 'logo',
              value: client && client.logos ? { saved: client.logos.medium } : { saved: null },
              label: 'nouns.logo',
              maxSizeMb: 5,
              editable: this.canEditClient
            })
          : null,
        this.isGamelearnAgent
          ? new TextareaQuestion({
              key: 'comments',
              class: 'col-1',
              label: 'nouns.about',
              value: client ? client.comments : null,
              disabled: !this.canEditClient
            })
          : null,
        this.canCreateScorm
          ? new QuestionGroup({
              key: 'scorm',
              class: 'col-1',
              childQuestions: [
                new CheckboxQuestion({
                  key: 'enabled',
                  label: 'nouns.scormClient',
                  tooltipKey: 'scorm.enabled',
                  editable: true,
                  value: client?.scorm?.enabled ? true : false,
                  eventHandlers: {
                    onChange: (checked: boolean) => {
                      this.scormClientSubject.next(checked);
                      this.onScormEnabledChanged(checked);
                    }
                  }
                })
              ]
            })
          : null,
        this.getProductivityCfgQuestion(client)
      ].filter((q) => !!q)
    });
    return this.dataQuestionGroup;
  }

  getConfigurationQuestions(client?: Client, distributor?: Distributor) {
    this.configQuestionGroup = new QuestionGroup({
      title: 'clients.clientConfig',
      isSection: true,
      hideGroup: this.userRole.name === 'expert',
      childQuestions: [
        this.getSubscriptionQuestion(client, distributor),
        this.getPlanDetailsQuestions(client),
        this.getSelfRegistrationQuestion(client),
        this.getDisableLoginQuestion(client)
      ].filter((q) => !!q)
    });
    return this.hasChildQuestions(this.configQuestionGroup);
  }

  getSubscriptionActivationQuestions(client?: Client) {
    this.activationQuestionGroup = new QuestionGroup({
      key: 'dashboard',
      title: 'clients.dashboard.activation.title',
      isSection: true,
      childQuestions: [this.getSubscriptionActivationQuestion(client)].filter((q) => !!q)
    });
    return this.hasChildQuestions(this.activationQuestionGroup);
  }

  getRankingQuestion(client?: Client, distributor?: Distributor) {
    const rankingType =
      client && client.ranking && client.ranking.showAlias
        ? 'showAlias'
        : client && client.ranking && client.ranking.obfuscateRanking
        ? 'obfuscateRanking'
        : 'showRanking';

    // Gamification active/deactivate
    this.gamificationSubject.next(client && client['subscriptions'] ? client['subscriptions']['users'] : true);

    return new QuestionGroup({
      title: 'clients.ranking',
      key: 'rankingConfig',
      isSection: true,
      childQuestions: [
        new DropdownQuestion({
          key: 'config',
          label: 'clients.rankingTitle',
          class: 'col-2-offset',
          value: rankingType,
          editable: this.canEditClient,
          options: [
            { key: 'showRanking', value: this.translateService.instant('properties.showRanking') },
            { key: 'obfuscateRanking', value: this.translateService.instant('properties.obfuscateRanking') },
            { key: 'showAlias', value: this.translateService.instant('properties.showAlias') }
          ]
        }),
        this.getGamificationQuestion(client, distributor)
      ].filter((q) => q !== null)
    });
  }

  getTermsQuestion(client?: Client) {
    // TODO: Delete when database is consistent
    const termClient: { enabled: boolean; urls: any } = client?.terms || { enabled: false, urls: null };
    return this.isGamelearnAgent
      ? new QuestionGroup({
          title: 'clients.terms',
          key: 'terms',
          isSection: true,
          childQuestions: [
            new CheckboxQuestion({
              key: 'enabled',
              label: 'clients.termsEnabled',
              class: 'col-1',
              value: client?.terms?.enabled,
              eventHandlers: {
                onChange: (checked: boolean) => {
                  const question = this.utilsQuestionsService.findQuestion('terms', this.questions);
                  const childQuestion = question.getChildQuestion('urls');

                  childQuestion.isDropdown = !checked;
                  TERMS_URL_LANGS.forEach((lang: string) => {
                    const langQuestion = childQuestion.getChildQuestion(lang);
                    langQuestion.editable = checked;
                    langQuestion.validators = { required: checked };
                    if (!checked) {
                      langQuestion.control.setErrors(null);
                      langQuestion.control.setValidators(null);
                    } else {
                      langQuestion.control.setValidators([Validators.required]);
                    }
                  });
                }
              }
            }),
            new QuestionGroup({
              title: 'clients.urls',
              key: 'urls',
              isDropdown: !termClient.enabled,
              childQuestions: [
                ...TERMS_URL_LANGS.map((lang: string) => {
                  return new TextboxQuestion({
                    key: lang,
                    editable: termClient.enabled,
                    label: `languageCodes.${lang}`,
                    class: 'col-2-offset',
                    value: termClient.urls ? termClient.urls[lang] : '',
                    validators: { required: termClient.enabled }
                  });
                })
              ]
            })
          ].filter((q) => q !== null)
        })
      : null;
  }

  getCatalogueQuestionGroup(client?: Client, catalogueData?: any) {
    this.catalogueQuestionGroup = new QuestionGroup({
      title: 'clients.config.catalogueSection',
      key: 'catalogueData',
      isSection: true,
      childQuestions: [
        this.getDefaultAccessModeQuestion(client, catalogueData),
        new QuestionGroup({
          key: 'configuration',
          childQuestions: [
            new DropdownQuestion({
              key: 'shouldSendDiploma',
              label: 'clients.config.diploma.label',
              class: 'col-2-offset',
              value: JSON.stringify(catalogueData?.configuration?.shouldSendDiploma ?? true),
              editable: this.canEditClient,
              options: [
                { key: 'true', value: this.translateService.instant('clients.config.diploma.enabled') },
                { key: 'false', value: this.translateService.instant('clients.config.diploma.disabled') }
              ]
            })
          ]
        })
      ].filter((q) => q !== null)
    });

    return this.catalogueQuestionGroup;
  }

  getDefaultAccessModeQuestion(client?: Client | any, catalogueData?: any) {
    let value = catalogueData?.defaultAccessMode;
    if (!value) {
      value = !client?.scorm?.enabled && client?.subscriptions?.credits ? 'limited' : 'open';
    }

    return new DropdownQuestion({
      key: 'defaultAccessMode',
      label: 'clients.config.defaultAccessMode',
      class: 'col-2-offset',
      value,
      editable: this.canEditClient && !client?.scorm?.enabled,
      options: [
        { key: 'open', value: this.translateService.instant('courses.config.accessModes.open') },
        { key: 'limited', value: this.translateService.instant('courses.config.accessModes.limited') },
        { key: 'unavailable', value: this.translateService.instant('courses.config.accessModes.unavailable') }
      ]
    });
  }

  getSelfRegistrationQuestion(client?: Client) {
    return new QuestionGroup({
      key: 'selfRegistration',
      childQuestions: [
        new CheckboxQuestion({
          key: 'enabled',
          label: 'clients.enableSelfRegistration',
          class: 'col-1',
          disabled: !this.canEditClient,
          editable: !client?.scorm?.enabled,
          value: client && client.selfRegistration ? !!client.selfRegistration.enabled : false
        })
      ]
    });
  }

  getSubscriptionActivationQuestion(client?: Client) {
    return new QuestionGroup({
      key: 'activation',
      childQuestions: [
        new CheckboxQuestion({
          key: 'enabled',
          label: 'clients.dashboard.activation.enabled',
          class: 'col-1',
          value: !!client?.dashboard?.activation?.enabled || false
        }),
        new TextboxQuestion({
          key: 'goal',
          class: 'col-2-offset',
          tooltip: 'clients.dashboard.activation.tooltip',
          label: 'clients.dashboard.activation.goal',
          value: client?.dashboard?.activation?.goal
        })
      ]
    });
  }

  getDisableLoginQuestion(client?: Client) {
    if (this.profileService.canExecute('clients.disableLogin')) {
      return new CheckboxQuestion({
        key: 'disableLogin',
        label: 'clients.disableLogin',
        class: 'col-1',
        value: !!client?.disableLogin
      });
    }
  }

  getNotificationsConfig(client?: Client, distributor?: Distributor) {
    if (!this.isGamelearnAgent && !this.isPartnerAgent) return null;
    this.notificationsQuestionGroup = new QuestionGroup({
      title: 'notifications.studentsConfig',
      isSection: true,
      class: 'col-1',
      childQuestions: [
        new DropdownQuestion({
          key: 'lang',
          label: 'notifications.language',
          placeholder: 'actions.selectLanguage',
          class: 'col-2-offset',
          editable: !client?.scorm?.enabled,
          value: client && client.lang ? client.lang : null,
          options: this.locales.getExtendedOptions(),
          validators: { required: true }
        }),
        this.getNotificationsQuestions(client, distributor)
      ]
    });
    return this.notificationsQuestionGroup;
  }

  onScormEnabledChanged(checked: boolean) {
    this.setFormCheckQuestionsByScorm('roleplay', ['enabled'], !checked, false);
    this.setFormCheckQuestionsByScorm('kepler', ['enabled'], !checked, false);
    this.setFormCheckQuestionsByScorm('mars', ['enabled'], !checked, false);
    this.setFormCheckQuestionsByScorm(
      'rankingConfig',
      ['showGamification'],
      !checked && (this.isCsOrRoot || this.userRole.name === 'distributorManager'),
      false
    );
    this.setFormCheckQuestionsByScorm('notificationsCfg', ['referrals'], !checked, !checked);
    this.setFormCheckQuestionsByScorm('n1', ['enabled'], !checked, !checked);
    this.setFormCheckQuestionsByScorm('n3', ['enabled'], !checked, !checked);
    this.setSelectQuestionsFormByScorm(['weekPeriodicity'], checked, checked);
    this.setFormCheckQuestionsByScorm('n4', ['enabled'], !checked, !checked);
    this.setSelectQuestionsFormByScorm(['fundaeDiploma'], checked, checked, true);
    this.setFormCheckQuestionsByScorm('n8', ['enabled'], !checked, !checked);
    this.setFormCheckQuestionsByScorm('push', ['enabled'], !checked, !checked);
    this.setFormCheckQuestionsByScorm(
      'selfRegistration',
      ['enabled'],
      !checked && this.userRole.name !== 'distributor',
      checked
    );
    this.setSelectQuestionsFormByScorm(['lang'], checked, checked);
    this.setFormCheckQuestionsByScorm('allNotifications', ['disabledNotifications'], !checked, false);

    const subscriptionsQuestion = this.utilsQuestionsService.findQuestion(
      'subscriptions',
      this.subscriptionQuestionGroup.childQuestions
    );
    const clientData = { subscriptions: subscriptionsQuestion.control.value, scorm: { enabled: checked } };

    const defaultAccessModeIndex = this.utilsQuestionsService.findQuestionIndex(
      'defaultAccessMode',
      this.catalogueQuestionGroup.childQuestions
    );
    const defaultAccessModeQuestion = this.getDefaultAccessModeQuestion(
      clientData,
      checked ? { defaultAccessMode: 'open' } : null
    );
    this.catalogueQuestionGroup.childQuestions[defaultAccessModeIndex] = defaultAccessModeQuestion;
  }

  resetNotificationsCfg() {
    this.notificationsCfg.n1.enabled = false;
    this.notificationsCfg.n3.enabled = false;
    this.notificationsCfg.n4.enabled = false;
    this.notificationsCfg.n8.enabled = false;
    this.notificationsCfg.push.enabled = false;
    this.referralsCfg = false;
  }

  setNotificationsCfg(notification, value) {
    if (this.scormClientSubject.getValue()) return;
    const disabledNotificationsQuestion = this.utilsQuestionsService.findQuestion(
      'disabledNotifications',
      this.questions
    );
    const disabledNotificationsValue = disabledNotificationsQuestion.value;
    if (!disabledNotificationsValue || value) {
      if (disabledNotificationsValue) {
        this.resetNotificationsCfg();
      }
      this.notificationsCfg[notification].enabled = value;
    }
  }

  setReferralsCfg(value) {
    if (this.scormClientSubject.getValue()) return;
    const disabledNotificationsQuestion = this.utilsQuestionsService.findQuestion(
      'disabledNotifications',
      this.questions
    );
    const disabledNotificationsValue = disabledNotificationsQuestion.value;
    if (!disabledNotificationsValue || value) {
      if (disabledNotificationsValue) {
        this.resetNotificationsCfg();
      }
      this.referralsCfg = value;
    }
  }

  getNotificationsQuestions(client?: Client, distributor?: Distributor) {
    const creditsClient = client
      ? client.subscriptions?.credits
      : distributor
      ? distributor.subscriptions?.credits
      : false;
    const notificationsCfg = client && client.notificationsCfg ? client.notificationsCfg : DEFAULT_NOTIFICATIONS_CFG;
    const isEnabledReferrals = client ? client.referrals : true;
    return new QuestionGroup({
      key: 'notificationsCfg',
      class: 'col-1',
      childQuestions: [
        new QuestionGroup({
          key: 'n1',
          class: 'col-1',
          hidden: creditsClient,
          childQuestions: [
            new CheckboxQuestion({
              key: 'enabled',
              class: 'col-1',
              editable: !client?.scorm?.enabled,
              tooltipKey: 'notificationsCfg.n1.enabled',
              label: 'notifications.config.campusAcountActivation',
              value: notificationsCfg.n1.enabled,
              eventHandlers: {
                onChange: (value) => {
                  this.setNotificationsCfg('n1', value);
                  if (value) this.setValueDisabledNotification(value);
                }
              }
            })
          ]
        }),
        new QuestionGroup({
          key: 'n4',
          class: 'col-1',
          childQuestions: [
            new CheckboxQuestion({
              key: 'enabled',
              tooltipKey: 'notificationsCfg.n4.enabled',
              class: 'col-1',
              editable: !client?.scorm?.enabled,
              label: 'notifications.config.playtimeExtension',
              value: notificationsCfg.n4.enabled,
              eventHandlers: {
                onChange: (value) => {
                  this.setNotificationsCfg('n4', value);
                  if (value) this.setValueDisabledNotification(value);
                }
              }
            })
          ]
        }),
        new QuestionGroup({
          key: 'n3',
          class: 'col-1',
          childQuestions: [
            new CheckboxQuestion({
              key: 'enabled',
              tooltipKey: 'notificationsCfg.n3.enabled',
              class: 'col-1',
              editable: !client?.scorm?.enabled,
              label: 'notifications.config.studentsEncourage',
              value: notificationsCfg.n3.enabled,
              eventHandlers: {
                onChange: (value) => {
                  this.setNotificationsCfg('n3', value);
                  if (value) this.setValueDisabledNotification(value);
                }
              }
            })
          ]
        }),
        this.getPushNotificationsQuestion(notificationsCfg, client, distributor),
        this.getKirkpatrickQuestion(notificationsCfg, client, distributor),
        this.getReferralQuestion(isEnabledReferrals, client, distributor),
        this.getDisableNotificationsQuestion(client),
        new DropdownQuestion({
          key: 'weekPeriodicity',
          tooltipKey: 'notificationsCfg.n3.weekPeriodicity',
          label: 'notifications.config.studentsEncouragePeriodicity',
          class: 'col-2-offset',
          editable: !client?.scorm?.enabled,
          value: notificationsCfg.n3.weekPeriodicity,
          options: [
            { key: 1, value: this.translateService.instant('calendar.weeks.1') },
            { key: 2, value: this.translateService.instant('calendar.weeks.2') },
            { key: 4, value: this.translateService.instant('calendar.weeks.4') }
          ]
        }),
        this.getN6Question(client, notificationsCfg)
      ]
    });
  }

  getPushNotificationsQuestion(notificationsCfg?: any, client?: Client, distributor?: Distributor) {
    if (this.isCsOrRoot && !distributor) {
      return new QuestionGroup({
        key: 'push',
        class: 'col-1',
        childQuestions: [
          new CheckboxQuestion({
            key: 'enabled',
            tooltipKey: 'notificationsCfg.push.enabled',
            class: 'col-1',
            editable: !client?.scorm?.enabled,
            label: 'notifications.config.push',
            value: notificationsCfg?.push?.enabled,
            eventHandlers: {
              onChange: (value) => {
                this.setNotificationsCfg('push', value);
                if (value) this.setValueDisabledNotification(value);
              }
            }
          })
        ]
      });
    }
    return new HiddenQuestion({
      key: 'push',
      value: { enabled: false }
    });
  }

  getDisableNotificationsQuestion(client?: Client, enableNotifications?: boolean) {
    const notificationsCfg = client && client.notificationsCfg ? client.notificationsCfg : DEFAULT_NOTIFICATIONS_CFG;
    const referralsCfg = client ? client.referrals : true;
    const value = enableNotifications
      ? false
      : !notificationsCfg.n1.enabled &&
        !notificationsCfg.n3.enabled &&
        !notificationsCfg.n4.enabled &&
        !notificationsCfg.n8.enabled &&
        !notificationsCfg.push.enabled &&
        !referralsCfg &&
        !client?.scorm?.enabled;
    return new QuestionGroup({
      key: 'allNotifications',
      class: 'col-1',
      childQuestions: [
        new CheckboxQuestion({
          key: 'disabledNotifications',
          tooltipKey: 'notificationsCfg.disabled',
          class: 'col-1',
          editable: !client?.scorm?.enabled,
          label: 'notifications.config.disabled',
          value,
          eventHandlers: {
            onChange: (value) => {
              this.setValueNotifications(value);
            }
          }
        })
      ]
    });
  }

  getAgentsQuestions(client?: Client) {
    if (!this.isGamelearnAgent) return null;
    const agentsList = client ? client.agentIds : [];
    const agentQuestionDisabled =
      this.userRole.group !== 'cs' &&
      !['superAdmin', 'salesChief', 'distributor', 'expert', 'distributorManager'].includes(this.userRole.name);
    this.adminsQuestionGroup = new QuestionGroup({
      title: 'clients.clientAdmin',
      isSection: true,
      controlType: 'agents',
      key: 'agentIds',
      value: agentsList,
      childQuestions: [
        new AgentsQuestion({
          key: 'selectAgents',
          class: 'col-1',
          label: 'nouns.cs',
          agents: this.agentsCache.getAgents(this.agentsCache.ROLES.CS),
          value: agentsList,
          disabled: agentQuestionDisabled
        }),
        new AgentsQuestion({
          key: 'selectSales',
          class: 'col-1',
          label: 'nouns.salesmans',
          agents: this.agentsCache.getAgents(this.agentsCache.ROLES.SALES),
          value: agentsList,
          disabled: agentQuestionDisabled
        }),
        new AgentsQuestion({
          key: 'selectDistributors',
          class: 'col-1',
          label: 'nouns.distributors',
          agentsObservable: this.agentsCache.getDistributorAgents(),
          value: agentsList,
          disabled: !this.canEditClient
        })
      ]
    });
    return this.adminsQuestionGroup;
  }

  getSubscriptionQuestion(client?: Client, distributor?: Distributor) {
    if (!this.isGamelearnAgent && !this.canCreateScorm) return null;
    const credits = { credits: true, users: false };
    const users = { credits: false, users: true };

    const enableSubscriptionChange =
      client &&
      (distributor
        ? this.userRole.name === 'distributorManager' && distributor.subscriptions.users && client.subscriptions.credits
        : this.isGamelearnAgent);

    this.subscriptionQuestionGroup = new QuestionGroup({
      childQuestions: [
        this.getClientPlanBtn(client, !enableSubscriptionChange),
        new DropdownQuestion({
          key: 'subscriptions',
          label: 'clients.header.subscriptions',
          class: enableSubscriptionChange ? 'col-2' : 'col-2-offset',
          options: [
            { value: this.translateService.instant('subscriptions.credits'), key: credits },
            { value: this.translateService.instant('subscriptions.users'), key: users }
          ],
          value: client
            ? client.subscriptions.credits
              ? credits
              : users
            : distributor && distributor.subscriptions.credits
            ? credits
            : users,
          editable: !client && !distributor,
          eventHandlers: {
            onChange: (value) => {
              const notificationsCfgQuestion = this.utilsQuestionsService.findQuestion(
                'notificationsCfg',
                this.questions
              );
              // Delete the n1 notification for credits
              if (notificationsCfgQuestion) {
                notificationsCfgQuestion.childQuestions[0].hidden = value['credits'];
                notificationsCfgQuestion.childQuestions[0].childQuestions[0].value = this.scormClientSubject.getValue()
                  ? false
                  : !value['credits'];
              }

              // Set defaultAccessMode to 'limited' on non-SCORM credits client creation
              const scormQuestionGroup = this.utilsQuestionsService.findQuestion(
                'scorm',
                this.dataQuestionGroup.childQuestions
              );
              const scormQuestion = this.utilsQuestionsService.findQuestion(
                'enabled',
                scormQuestionGroup.childQuestions
              );
              const isScormEnabled = scormQuestion.control.value;

              if (!isScormEnabled) {
                const defaultAccessModeQuestion = this.utilsQuestionsService.findQuestion(
                  'defaultAccessMode',
                  this.questions
                );
                defaultAccessModeQuestion.value = value['credits'] ? 'limited' : 'open';
              }

              // Ranking/Gamification
              this.gamificationSubject.next(value['users']);
              this.setGamificationStudents(value['credits']);
            }
          }
        })
      ]
    });
    return this.subscriptionQuestionGroup;
  }

  /**
   * Draw the decorator button for the client plan
   * @param client
   * @param isHidden
   */
  getClientPlanBtn(client?: Client, isHidden?: boolean): any {
    return new Decorator({
      key: 'changePlan',
      class: 'col-2-offset',
      hidden: isHidden || false,
      label: client && client.subscriptions.credits ? 'subscriptions.changeToUsers' : 'subscriptions.changeToCredits',
      buttonType: 'primary',
      eventHandlers: {
        click: () => this.changeSubscriptions(client)
      }
    });
  }

  onEditorAccessChange(editorAccess: boolean, client?: Client) {
    this.editorAccess = editorAccess;
    const planQuestionIndex = this.utilsQuestionsService.findQuestionIndex(
      'plan',
      this.configQuestionGroup.childQuestions
    );
    const newPlanQuestion = this.getPlanDetailsQuestions(client);
    this.configQuestionGroup.childQuestions[planQuestionIndex] = newPlanQuestion;
  }

  getPlanDetailsQuestions(client: Client) {
    if (!this.isGamelearnAgent) return null;
    const isPlanEditable = !client || !client.stripeCustomerId;
    const plan = client && client.plan ? client.plan : {};
    const timezone = client ? client.timezone : 'Etc/UTC';
    let startDate =
      this.editorAccess && plan.startDate
        ? new Date(plan.startDate)
        : !client
        ? DateTime.local().setZone(timezone).startOf('day').toJSDate()
        : null;
    let endDate =
      this.editorAccess && plan.endDate
        ? new Date(plan.endDate)
        : !client
        ? DateTime.fromJSDate(startDate).setZone(timezone).plus({ year: 1, seconds: -1 }).toJSDate()
        : null;

    if (!this.editorAccess) {
      startDate = null;
      endDate = null;
    }

    this.planQuestionGroup = new QuestionGroup({
      key: 'plan',
      class: 'col-1 hidden-group-question',
      childQuestions: [
        new HiddenQuestion({
          key: 'type',
          class: 'col-2-offset',
          label: 'nouns.planType',
          value: this.editorAccess ? client && client.plan && client.plan.type : NO_EDITOR_PLAN,
          placeholder: 'nouns.plans.none',
          optionsObservable: this.plansService.getBasePlanOptionsObservable(),
          editable: this.canEditPlan && isPlanEditable && this.userRole.name !== 'distributor',
          eventHandlers: {
            onChange: (value) => {
              const newClient = {
                ...client,
                plan: {
                  ...plan,
                  type: value
                }
              };
              this.onEditorAccessChange(value !== NO_EDITOR_PLAN, newClient);
            }
          }
        }),
        new HiddenQuestion({
          key: 'editorAccess',
          value: this.editorAccess
        }),
        new HiddenQuestion({
          key: 'scope',
          value: 'corporate'
        }),
        new HiddenQuestion({
          key: 'startDate',
          label: 'nouns.startDate',
          class: 'col-2',
          disabled: !this.editorAccess,
          editable: this.canEditPlan && isPlanEditable,
          value: startDate,
          validators: { required: this.editorAccess },
          eventHandlers: {
            onChange: (newStartDate) => {
              if (newStartDate) {
                this.setEndDate(
                  'endDate',
                  this.planQuestionGroup.childQuestions,
                  newStartDate,
                  this.timezoneSubject.getValue()
                );
              }
            }
          }
        }),
        new HiddenQuestion({
          key: 'endDate',
          label: 'nouns.endDate',
          class: 'col-2',
          disabled: !this.editorAccess,
          editable: this.canEditPlan && isPlanEditable,
          defaultHour: 23,
          defaultMinute: 59,
          defaultSeconds: 59,
          value: endDate,
          validators: { required: this.editorAccess }
        })
      ]
    });
    return this.planQuestionGroup;
  }

  getDistributorQuestion(client?: Client, distributor?: Distributor) {
    const distributorId = client ? client.distributorId : distributor ? distributor.id : null;
    const questionOptions = {
      distributor: distributorId ? { id: distributorId } : null,
      editable: !client,
      entity: distributor,
      entityOptions: {
        preload: distributorId
      },
      validators: { required: true },
      eventHandlers: {
        onSelected: (selectedDistributor) => {
          this.agentsCache.updateDistributorAgents(selectedDistributor ? selectedDistributor.id : null);
        }
      }
    };
    return this.questionsFactory.getDistributorQuestion(questionOptions);
  }

  getProductivityCfgQuestion(client?: Client) {
    const productivityCfgQuestion = new QuestionGroup({
      key: 'productivityCfg',
      childQuestions: [
        this.profileService.canExecute('kepler.enable')
          ? new QuestionGroup({
              key: 'kepler',
              class: 'col-1',
              childQuestions: [
                new CheckboxQuestion({
                  key: 'enabled',
                  tooltipKey: 'kepler.enabled',
                  class: 'col-1',
                  editable: !client?.scorm?.enabled,
                  label: 'nouns.productivityCfg.kepler',
                  value: !!client?.productivityCfg?.kepler?.enabled
                })
              ]
            })
          : null,
        this.profileService.canExecute('mars.enable')
          ? new QuestionGroup({
              key: 'mars',
              class: 'col-1',
              childQuestions: [
                new CheckboxQuestion({
                  key: 'enabled',
                  tooltipKey: 'mars.enabled',
                  class: 'col-1',
                  editable: !client?.scorm?.enabled,
                  label: 'nouns.productivityCfg.mars',
                  value: !!client?.productivityCfg?.mars?.enabled
                })
              ]
            })
          : null,
        this.profileService.canExecute('roleplay.enable')
          ? new QuestionGroup({
              key: 'roleplay',
              class: 'col-1',
              childQuestions: [
                new CheckboxQuestion({
                  key: 'enabled',
                  tooltipKey: 'roleplay.enabled',
                  class: 'col-1',
                  editable: !client?.scorm?.enabled,
                  label: 'nouns.productivityCfg.roleplay',
                  value: !!client?.productivityCfg?.roleplay?.enabled
                })
              ]
            })
          : null
      ].filter((q) => !!q)
    });
    return productivityCfgQuestion.childQuestions.length ? productivityCfgQuestion : null;
  }

  private getReferralQuestion(isReferredStudentsClient: boolean, client?: Client, distributor?: Distributor) {
    const editable = !client?.scorm?.enabled;
    if (this.isCsOrRoot && !distributor) {
      return new CheckboxQuestion({
        key: 'referrals',
        class: 'col-1',
        label: 'clients.referrals.label',
        value: isReferredStudentsClient,
        editable,
        eventHandlers: {
          onChange: (value) => {
            const disabledNotificationsQuestion = this.utilsQuestionsService.findQuestion(
              'disabledNotifications',
              this.questions
            );
            const disabledNotificationsValue = disabledNotificationsQuestion.value;
            if (!this.scormClientSubject.getValue() && !disabledNotificationsValue) {
              this.changeReferral(value);
            }
            this.setReferralsCfg(value);
            if (value) this.setValueDisabledNotification(value);
          }
        }
      });
    }
    return new HiddenQuestion({
      key: 'referrals',
      value: isReferredStudentsClient
    });
  }

  private getKirkpatrickQuestion(notificationsCfg?: any, client?: Client, distributor?: Distributor) {
    if (!distributor && this.userRole.name !== 'distributorManager') {
      return new QuestionGroup({
        key: 'n8',
        class: 'col-1',
        childQuestions: [
          new CheckboxQuestion({
            key: 'enabled',
            tooltipKey: 'notificationsCfg.n8.enabled',
            class: 'col-1',
            editable: !client?.scorm?.enabled,
            label: 'notifications.config.kirkpatrick',
            value: notificationsCfg?.n8?.enabled,
            eventHandlers: {
              onChange: (value) => {
                this.setNotificationsCfg('n8', value);
                if (value) this.setValueDisabledNotification(value);
              }
            }
          })
        ]
      });
    }

    return new HiddenQuestion({
      key: 'n8',
      value: { enabled: false } // N8 is only for corporate clients
    });
  }

  /**
   * Get the gamification question when client is the subscription type and enabled for CS's group
   * @param client
   * @param distributor
   * @returns CheckboxQuestion or null
   */
  private getGamificationQuestion(client, distributor?): CheckboxQuestion {
    const showGamification =
      !distributor ||
      (distributor?.type === 'distributor' &&
        distributor?.subscriptions?.users &&
        (!client || client?.subscriptions?.users));
    if (!showGamification) return null;
    const value = client ? client.gamification.enabled : false;
    return this.gamificationSubject.getValue()
      ? new CheckboxQuestion({
          key: 'showGamification',
          label: 'clients.showGamification',
          class: 'col-1',
          disabled: !(this.isCsOrRoot || this.userRole.name === 'distributorManager') || client?.scorm?.enabled,
          editable: !client?.scorm?.enabled && (this.isCsOrRoot || this.userRole.name === 'distributorManager'),
          value,
          eventHandlers: {
            onChange: (value) => {
              if (!this.scormClientSubject.getValue()) {
                this.changeGamification(value);
              }
            }
          }
        })
      : null;
  }

  private getN6Question(client, notificationsCfg): DropdownQuestion {
    const value = notificationsCfg.n6?.enabled
      ? notificationsCfg.n6.includeSurvey
        ? 'survey'
        : 'diploma'
      : 'inactive';

    return new DropdownQuestion({
      key: 'fundaeDiploma',
      tooltipKey: 'notificationsCfg.n6.enabled',
      label: 'notifications.config.fundaeDiploma.label',
      class: 'col-2-offset',
      editable: !client?.scorm?.enabled,
      value,
      options: [
        { key: 'inactive', value: this.translateService.instant('notifications.config.fundaeDiploma.inactive') },
        { key: 'diploma', value: this.translateService.instant('notifications.config.fundaeDiploma.diploma') },
        { key: 'survey', value: this.translateService.instant('notifications.config.fundaeDiploma.survey') }
      ]
    });
  }

  private showNotificationReferral(questions: any, childIndex: number) {
    this.popupService
      .confirm({
        title: this.translateService.instant('clients.referrals.title'),
        text: this.translateService.instant('clients.referrals.body'),
        confirmButtonText: this.translateService.instant('clients.referrals.confirm'),
        cancelButtonText: this.translateService.instant('clients.referrals.cancel'),
        type: 'question'
      })
      .then(() => {
        questions.splice(childIndex, 1, this.getReferralQuestion(false));
        this.referralsCfg = false;
      })
      .catch(() => {
        questions.splice(childIndex, 1, this.getReferralQuestion(true));
        this.referralsCfg = true;
      });
  }

  /**
   * Change and find of the referral question
   * @param value of the checkbox
   * @private
   */
  private changeReferral(value: boolean): void {
    if (this.referralsCfg && !value) {
      const notificationsCfgQuestion = this.utilsQuestionsService.findQuestion('notificationsCfg', this.questions);
      const foundQuestion = this.utilsQuestionsService.findQuestionNestedIndex('referrals', [notificationsCfgQuestion]);
      if (foundQuestion) {
        this.showNotificationReferral(foundQuestion.childQuestions, foundQuestion.childIndex);
      }
    }
  }

  private setValueNotifications(value): void {
    if (this.scormClientSubject.getValue()) return;
    this.setFormCheckQuestionsByScorm('notificationsCfg', ['referrals'], true, value ? !value : this.referralsCfg);
    this.setFormCheckQuestionsByScorm('n1', ['enabled'], true, value ? !value : this.notificationsCfg.n1.enabled);
    this.setFormCheckQuestionsByScorm('n3', ['enabled'], true, value ? !value : this.notificationsCfg.n3.enabled);
    this.setFormCheckQuestionsByScorm('n4', ['enabled'], true, value ? !value : this.notificationsCfg.n4.enabled);
    this.setFormCheckQuestionsByScorm('n8', ['enabled'], true, value ? !value : this.notificationsCfg.n8.enabled);
    this.setFormCheckQuestionsByScorm('push', ['enabled'], true, value ? !value : this.notificationsCfg.push.enabled);
    this.setSelectQuestionsFormByScorm(['fundaeDiploma'], false, value, true);

    this.setFormCheckQuestionsByScorm('allNotifications', ['disabledNotifications'], true, value);
  }

  private setValueDisabledNotification(value): void {
    this.setFormCheckQuestionsByScorm('allNotifications', ['disabledNotifications'], true, !value);
  }

  /**
   * Show Notification of the ranking and gamification of students
   * @param questions
   * @param childIndex
   * @private
   */
  private showNotificationGamification(questions: any, childIndex: number) {
    this.popupService
      .openPopup({
        title: this.translateService.instant('clients.gamification.title'),
        html: this.translateService.instant('clients.gamification.body'),
        confirmButtonText: this.translateService.instant('clients.gamification.confirm'),
        cancelButtonText: this.translateService.instant('clients.gamification.cancel'),
        class: 'center',
        type: 'error',
        icon: 'glicon-alert-thin',
        size: 'medium',
        showCancelButton: true,
        callbackToClose: true
      })
      .then(() => {
        questions.splice(childIndex, 1, this.getGamificationQuestion({ gamification: { enabled: true } }, false));
        this.isGamificationEnabled = true;
      })
      .catch(() => {
        questions.splice(childIndex, 1, this.getGamificationQuestion({ gamification: { enabled: false } }, false));
        this.isGamificationEnabled = false;
      });
  }

  /**
   * Change and find of the gamification question
   * @param value
   * @private
   */
  private changeGamification(value: boolean): void {
    if (this.isGamificationEnabled && !value) {
      const foundQuestion = this.utilsQuestionsService.findQuestionNestedIndex('showGamification', this.questions);
      if (foundQuestion) {
        this.showNotificationGamification(foundQuestion.childQuestions, foundQuestion.childIndex);
      }
    } else {
      this.isGamificationEnabled = value;
    }
  }

  /**
   * Set the end date of the question form
   * @param fieldQuestion field to change
   * @param questions list of questions of the form
   * @param startDate selected the new date
   * @param timezone client timezone
   */
  private setEndDate(fieldQuestion: string, questions: any, startDate: any, timezone: any): void {
    const endDate = this.utilsQuestionsService.findQuestion(fieldQuestion, questions);
    const tempEndDate = DateTime.fromJSDate(startDate).setZone(timezone).plus({ year: 1, seconds: -1 }).toJSDate();
    endDate.control.setValue(tempEndDate);
  }

  /**
   * Check if the question group is empty
   * @param questionGroup,
   * @returns the question group if the question group is not empty, null if is empty
   */
  private hasChildQuestions(questionGroup: QuestionGroup) {
    return questionGroup.childQuestions.length > 0 ? questionGroup : null;
  }

  /**
   * Set the value of gamification in ranking group
   * @param isCreditClient boolean
   */
  private setGamificationStudents(isCreditClient: boolean): void {
    const rankingStudentGroup = this.utilsQuestionsService.findQuestion('rankingConfig', this.questions);
    if (rankingStudentGroup.childQuestions[1]) rankingStudentGroup.childQuestions[1].hidden = isCreditClient;
  }

  /**
   * Set the editable property depending of the checked param value and set the value
   *  of the question to false
   * @param questionKeys list of string
   * @param checked boolean
   */
  private setCheckQuestionsByScorm(questionKeys: string[], checked: boolean) {
    questionKeys.forEach((key: string) => {
      const question = this.utilsQuestionsService.findQuestion(key, this.questions);
      if (question) {
        question.editable = !checked;
        question.control.setValue(!checked);
      }
    });
  }

  /**
   * Set the editable property depending of the checked param value and set the value
   *  of the question to the second value of the options
   * @param questionKeys list of string
   * @param checked boolean
   * @param value boolean
   */
  private setSelectQuestionsFormByScorm(
    questionKeys: string[],
    checked: boolean,
    value?: boolean,
    isNotification?: boolean
  ) {
    questionKeys.forEach((key) => {
      const question = this.utilsQuestionsService.findQuestion(key, this.questions);
      if (question) {
        question.editable = !checked;
        if (value) question.control.setValue(question.options[1].key);
        if (isNotification && value) question.control.setValue(question.options[0].key);
      }
    });
  }

  /**
   * Set the property editable of the childQuestions depending of the checked param
   *  value and set the value to the param value
   * @param questionKey string
   * @param childQuestions list of string
   * @param editable boolean
   * @param value boolean
   */
  private setFormCheckQuestionsByScorm(
    questionKey: string,
    childQuestions: string[],
    editable: boolean,
    value: boolean
  ) {
    const question = this.utilsQuestionsService.findQuestion(questionKey, this.questions);
    if (question) {
      childQuestions.forEach((key) => {
        const child = question.getChildQuestion(key);
        if (child) {
          child.editable = editable;
          child.control.setValue(value);
        }
      });
    }
  }
}
